import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { FullPageLoader } from '@/components/States';
import { useAuth } from '@/features/auth/useAuth';
import { SignInPage } from '@/features/auth/SignInPage';
import { AuthCallbackPage } from '@/features/auth/AuthCallbackPage';
import { MyWorkPage } from '@/features/work-inbox/MyWorkPage';
import { LandingPage } from '@/features/landing/LandingPage';

/**
 * Pages loaded on demand.
 *
 * @remarks
 * Sign-in, the OAuth callback and the work inbox are imported directly, because they are the
 * first thing every session touches - splitting them would trade a smaller bundle for a
 * spinner on the page everybody opens.
 *
 * Everything else is a separate chunk. The Gantt and the board carry the two heaviest
 * dependencies in the application - the chart and the drag-and-drop sensors - and most people
 * never open either. Loading them with the inbox meant a planner's tooling was downloaded by
 * every specialist who only wanted to see their own work.
 */
const TaskDetailPage = lazy(() =>
  import('@/features/work-inbox/TaskDetailPage').then((m) => ({ default: m.TaskDetailPage })),
);

const BoardPage = lazy(() =>
  import('@/features/board/BoardPage').then((m) => ({ default: m.BoardPage })),
);

const PhasesPage = lazy(() =>
  import('@/features/phases/PhasesPage').then((m) => ({ default: m.PhasesPage })),
);

const ProjectsPage = lazy(() =>
  import('@/features/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);

const GanttPage = lazy(() =>
  import('@/features/schedule/GanttPage').then((m) => ({ default: m.GanttPage })),
);

const WorkloadPage = lazy(() =>
  import('@/features/resourcing/WorkloadPage').then((m) => ({ default: m.WorkloadPage })),
);

const PeoplePage = lazy(() =>
  import('@/features/resourcing/PeoplePage').then((m) => ({ default: m.PeoplePage })),
);

const MyProfilePage = lazy(() =>
  import('@/features/resourcing/MyProfilePage').then((m) => ({ default: m.MyProfilePage })),
);

const PortfolioPage = lazy(() =>
  import('@/features/reporting/PortfolioPage').then((m) => ({ default: m.PortfolioPage })),
);

const ProjectDashboardPage = lazy(() =>
  import('@/features/reporting/ProjectDashboardPage').then((m) => ({
    default: m.ProjectDashboardPage,
  })),
);

const ReportsPage = lazy(() =>
  import('@/features/reporting/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);

const SyncHealthPage = lazy(() =>
  import('@/features/admin/SyncHealthPage').then((m) => ({ default: m.SyncHealthPage })),
);

const NotificationsPage = lazy(() =>
  import('@/features/admin/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);

const NotificationDetailPage = lazy(() =>
  import('@/features/admin/NotificationDetailPage').then((m) => ({
    default: m.NotificationDetailPage,
  })),
);

const AllTasksPage = lazy(() =>
  import('@/features/admin/AllTasksPage').then((m) => ({ default: m.AllTasksPage })),
);

const ProjectTasksPage = lazy(() =>
  import('@/features/admin/ProjectTasksPage').then((m) => ({ default: m.ProjectTasksPage })),
);

/**
 * Routing.
 *
 * @remarks
 * The gate below hides pages from people who are not signed in. It is a convenience, not a
 * control: every endpoint re-authorizes on the server, so bypassing this in the browser
 * yields a page that renders and then fails every request it makes.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInPage />} />

      {/* The public page, and the only route that renders for somebody with no session.
          Kept outside the gate below rather than inside it with a branch: a landing page
          that lived behind RequireSession would be one refactor away from being a page the
          gate forgets to let through. */}
      <Route path="/welcome" element={<LandingPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      <Route
        path="/*"
        element={
          <RequireSession>
            <AppShell>
              {/* Inside the shell rather than around it. A page that throws leaves the person
                  with the navigation still on screen, so they can go somewhere else - the
                  difference between one broken page and an unusable application. There was no
                  boundary at all until a task whose source record had been deleted was opened
                  and the whole application went blank. */}
              <RoutedErrorBoundary>
                {/* One Suspense boundary around the routes rather than one per page. A chunk
                    arrives in well under a second on an internal network, and a loader per
                    route would mean the shell repainting on every navigation. */}
                <Suspense fallback={<FullPageLoader />}>
                  <Routes>
                    <Route index element={<MyWorkPage />} />
                    <Route path="tasks/:id" element={<TaskDetailPage />} />
                    <Route path="board" element={<BoardPage />} />
                    <Route path="phases" element={<PhasesPage />} />
                    <Route path="projects" element={<ProjectsPage />} />
                    <Route path="schedule" element={<GanttPage />} />
                    <Route path="workload" element={<WorkloadPage />} />
                    <Route path="people" element={<PeoplePage />} />

                    {/* Reached from the account menu rather than the navigation. It is one
                        person's own record, not a place they work, so it does not belong
                        beside the boards and the plan. */}
                    <Route path="profile" element={<MyProfilePage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="portfolio" element={<PortfolioPage />} />
                    <Route path="project-dashboard" element={<ProjectDashboardPage />} />
                    <Route path="admin/tasks" element={<AllTasksPage />} />
                    <Route path="admin/project-tasks" element={<ProjectTasksPage />} />
                    <Route path="admin/notifications" element={<NotificationsPage />} />

                    {/* Its own route rather than an expanding row, so an administrator
                        looking into a complaint can send somebody the link to the exact
                        message they are discussing. */}
                    <Route
                      path="admin/notifications/:notificationId"
                      element={<NotificationDetailPage />}
                    />
                    <Route path="admin/sync" element={<SyncHealthPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </RoutedErrorBoundary>
            </AppShell>
          </RequireSession>
        }
      />
    </Routes>
  );
}

function RequireSession({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  // Restoring is a distinct state from signed out. Treating them the same would bounce a
  // returning user to the sign-in page for the moment it takes to exchange their refresh
  // token, which reads as being logged out at random.
  if (status === 'restoring') {
    return <FullPageLoader label="در حال بررسی نشست…" />;
  }

  if (status === 'signedOut') {
    // The landing page rather than the sign-in form. Somebody arriving at the root without a
    // session is at least as likely to be meeting the product for the first time as to be
    // signing back in, and the landing page offers signing in on every screenful.
    //
    // Deeper routes still go to the form: a link to a task is a person who knows what they
    // came for, and answering it with marketing would lose their destination.
    return <Navigate to={location.pathname === '/' ? '/welcome' : '/sign-in'} replace />;
  }

  return <>{children}</>;
}

/**
 * The error boundary, keyed by the current path.
 *
 * @remarks
 * Without the key the boundary is a trap rather than a safety net: once a page throws, its
 * error stays on screen and every page navigated to afterwards looks broken too. Remounting
 * on a path change clears it, so leaving the broken page is enough to recover.
 */
function RoutedErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}
